import { ChangeEvent, useEffect, useRef, useState } from "react";
import { uploadFileDirectly } from "../projects/AddProject";
import plusIconuplad from "../../assets/task/plus_icon_uload.svg";

interface BugResolveModalProps {
    defect: any;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (notes: string, fileUrl: string | null) => Promise<void>;
    isResolving: boolean;
    title?:any
}

export default function BugResolveModal({
    defect,
    isOpen,
    onClose,
    onSubmit,
    isResolving,
    title
}: BugResolveModalProps) {
    const [notes, setNotes] = useState("");
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("coverflow_hidden_scoll_bdy");
            return () => {
                document.body.classList.remove("coverflow_hidden_scoll_bdy");
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setNotes("");
            setFileUrl(null);
            setFileName(null);
            setValidationError(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;

        const token = localStorage.getItem("authToken")!;
        setIsUploading(true);
        setValidationError(null);

        try {
            const url = await uploadFileDirectly(file, file.name, token);
            setFileUrl(url);
            setFileName(file.name);
        } catch {
            setValidationError("File upload failed. Please try again.");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    async function handleSave() {
        if (!notes.trim() && !fileUrl) {
            setValidationError("Please provide either resolution notes or an attachment.");
            return;
        }
        setValidationError(null);
        await onSubmit(notes.trim(), fileUrl);
    }

    const isBusy = isUploading || isResolving;

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget && !isBusy) onClose();
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-md shadow-2xl w-full max-w-[560px] bugresolved-main-model">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="grid gap-2">
                        <h2 className="font-bold text-xl-dv text-black dark:text-white my-0">
                            Resolve {title}
                        </h2>
                        {defect?.screen_name && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 my-0 mt-0.5">
                                {defect.screen_name}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isBusy}
                        className=" text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 self-stretch grid grid-cols-1 mq1900:grid-cols-1 mq1425:grid-cols-1 mq950:grid-cols-1 mq825:grid-cols-1 mq575:grid-cols-1 flex-row items-start justify-start gap-[16px] mq1425:flex-wrap">
                    {/* Resolution Notes */}
                    <div className="grid gap-[8px] w-full">
                        <label className="text-sm font-semibold text-black dark:text-white">
                            Resolution Notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Resolution Notes..."
                            rows={4}
                            disabled={isBusy}
                            className="max-w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-black dark:text-white dark:bg-gray-700 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                        />
                    </div>

                    {/* Attachment */}
                    <div className="grid gap-[8px]">
                        <label className="text-sm font-semibold text-black dark:text-white">
                            Attachment
                        </label>

                        {fileUrl ? (
                            <div className="flex items-center justify-between border border-dashed border-gray-300 dark:border-gray-600 rounded-md px-3 py-2">
                                <span className="text-sm text-black dark:text-white truncate">{fileName}</span>
                                <button
                                    type="button"
                                    onClick={() => { setFileUrl(null); setFileName(null); }}
                                    disabled={isBusy}
                                    className="text-red-500 text-xs disabled:opacity-50   text-gray-400 cta-red modelinner-cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ) : isUploading ? (
                            <div className="w-full h-16 flex items-center justify-center border border-dashed border-gray-300 dark:border-gray-600 rounded-md">
                                <span className="text-sm text-blue-500">Uploading...</span>
                            </div>
                        ) : (
                            <div
                                className="w-full h-16 flex flex-col items-center justify-center border border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-blue-400 gap-[4px]"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <div className="bugimg-model-dv cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded"> <img src={plusIconuplad} alt="Upload" className="w-4 h-4" /></div>
                                <span className="text-sm text-gray-500 dark:text-gray-400">Click to upload</span>
                            </div>
                        )}

                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                        />
                    </div>

                    {validationError && (
                        <p className="text-red-500 text-sm my-0">{validationError}</p>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 pb-5 flex justify-end gap-[8px]">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isBusy}
                        className="cursor-pointer py-2.5 h-[44px] px-[42px] bg-white rounded-radiuscomponent-button box-border flex items-center justify-center border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 disabled:opacity-50"
                    >
                        <span className="text-sm font-body-xs-semi-bold text-surface-primary-surface">
                            Cancel
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isBusy}
                        className="cursor-pointer py-2.5 h-[44px] px-[42px] bg-surface-primary-surface rounded-radiuscomponent-button flex items-center justify-center hover:bg-dodgerblue disabled:opacity-50 [border:none]"
                    >
                        <span className="text-sm font-body-xs-semi-bold text-surface-bg-primary">
                            {isResolving ? "Saving..." : isUploading ? "Uploading..." : "Save"}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}
